import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import XpnsrPageHeader from './XpnsrPageHeader';
import SettingWarning from './SettingWarning';
import { Save, ShieldAlert } from 'lucide-react';

function toLines(arr) {
  return (arr || []).join('\n');
}
function fromLines(text) {
  return (text || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

export default function BotLists() {
  const { t } = useT();
  const [ips, setIps] = useState('');
  const [uas, setUas] = useState('');
  const [emptyUaIsBot, setEmptyUaIsBot] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(API + '/settings/bot-lists');
        setIps(toLines(res.data.custom_bot_ips));
        setUas(toLines(res.data.custom_bot_user_agents));
        setEmptyUaIsBot(res.data.empty_ua_is_bot !== false);
      } catch (err) {
        console.error('Error loading bot lists:', err);
        toast.error(t('Error loading bot lists'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await axios.put(API + '/settings/bot-lists', {
        custom_bot_ips: fromLines(ips),
        custom_bot_user_agents: fromLines(uas),
        empty_ua_is_bot: emptyUaIsBot,
      });
      setIps(toLines(res.data.custom_bot_ips));
      setUas(toLines(res.data.custom_bot_user_agents));
      setEmptyUaIsBot(res.data.empty_ua_is_bot !== false);
      toast.success(t('Bot lists saved'));
    } catch (err) {
      console.error('Error saving bot lists:', err);
      toast.error(t('Error saving bot lists'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Anti-fraud')}
        title={t('Bot lists')}
        subtitle={t('Custom bot IPs/CIDRs and User-Agent signatures used by the bot-detection fraud filter.')}
        actions={
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('Saving...') : t('Save')}
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('Bot IP addresses')}</CardTitle>
            <CardDescription>{t('One exact IP or CIDR block per line (e.g. 203.0.113.7 or 10.0.0.0/8).')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={ips}
              onChange={(e) => setIps(e.target.value)}
              rows={12}
              className="font-mono text-sm"
              placeholder="203.0.113.7"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('Bot User-Agent signatures')}</CardTitle>
            <CardDescription>{t('One per line: substring, mask (*) or /regex/.')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={uas}
              onChange={(e) => setUas(e.target.value)}
              rows={12}
              className="font-mono text-sm"
              placeholder="python-requests"
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <Label>{t('Treat empty User-Agent as bot')}</Label>
              <p className="text-xs text-muted-foreground">{t('Flag clicks that arrive with no User-Agent header.')}</p>
            </div>
          </div>
          <Switch checked={emptyUaIsBot} onCheckedChange={setEmptyUaIsBot} />
        </CardContent>
        {emptyUaIsBot && (
          <div className="px-6 pb-4">
            <SettingWarning testId="empty-ua-on">{t('Some real visitors come through apps and services that send no User-Agent. Their clicks will be counted as bots.')}</SettingWarning>
          </div>
        )}
      </Card>
    </div>
  );
}
